import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { showError } from "../../utils/toastUtils";
import ProductAPI from "../../api/inventory/ProductApi";

const COLUMNS = [
  { key: "Handle", level: "group" },
  { key: "Title", level: "product" },
  { key: "ProductType", level: "product" },
  { key: "ProductSKU", level: "product" },
  { key: "Brand", level: "product" },
  { key: "Category", level: "product" },
  { key: "Warehouse", level: "product" },
  { key: "HSNCode", level: "product" },
  { key: "Unit", level: "product" },
  { key: "GST", level: "product" },
  { key: "Status", level: "product" },
  { key: "Thumbnail", level: "product" },
  { key: "Images", level: "product" },
  { key: "ShortDescription", level: "product" },
  { key: "VariantName", level: "variant" },
  { key: "Variant SKU", level: "variant" },
  { key: "CostPrice", level: "variant" },
  { key: "Price", level: "variant" },
  { key: "MRP", level: "variant" },
  { key: "Discount", level: "variant" },
  { key: "Stock", level: "variant" },
  { key: "AlertQty", level: "variant" },
  { key: "Attr:Size", level: "variant" },
  { key: "Attr:Color", level: "variant" },
];

const PRODUCT_TYPES = ["Single", "Variant"];
const STATUSES = ["active", "draft", "inactive"];

const isNum = (v) => v !== "" && v != null && !isNaN(Number(v));
const num = (v) => Number(v);

function validateRows(rows) {
  const firstOfGroup = {};

  rows.forEach((r, i) => {
    const h = (r.Handle || "").trim();
    if (h && firstOfGroup[h] === undefined) firstOfGroup[h] = i;
  });

  const skuCount = {};

  rows.forEach((r) => {
    const s = (r.SKU || "").trim();
    if (s) skuCount[s] = (skuCount[s] || 0) + 1;
  });

  return rows.map((r, i) => {
    const e = {};
    const h = (r.Handle || "").trim();
    const isFirst = h && firstOfGroup[h] === i;

    if (!h) e.Handle = "Handle is required.";

    if (isFirst) {
      if (!(r.Title || "").trim()) e.Title = "Title is required.";
      if (r.ProductType && !PRODUCT_TYPES.includes(r.ProductType))
        e.ProductType = "Use Single or Variant.";
      if (r.Status && !STATUSES.includes(r.Status))
        e.Status = "Use active, draft, or inactive.";
    }

    if (!(r.SKU || "").trim()) e.SKU = "SKU is required.";
    else if (skuCount[(r.SKU || "").trim()] > 1)
      e.SKU = "Duplicate SKU in file.";

    if (!isNum(r.Price)) e.Price = "Price must be a number.";
    if (r.CostPrice !== "" && !isNum(r.CostPrice))
      e.CostPrice = "Must be a number.";
    if (r.MRP !== "" && r.MRP != null && !isNum(r.MRP))
      e.MRP = "Must be a number.";
    if (isNum(r.Price) && isNum(r.MRP) && num(r.Price) > num(r.MRP))
      e.MRP = "Price cannot exceed MRP.";
    if (
      r.Discount !== "" &&
      isNum(r.Discount) &&
      (num(r.Discount) < 0 || num(r.Discount) > 100)
    )
      e.Discount = "Must be 0-100.";
    if (r.Stock !== "" && isNum(r.Stock) && num(r.Stock) < 0)
      e.Stock = "Cannot be negative.";
    return e;
  });
}

const DISCARD_MSG = "You have unsaved rows. Discard them and close?";

function ImportProducts({ onImported }) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [rows, setRows] = useState([]);
  const [serverErrors, setServerErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const clientErrors = useMemo(() => validateRows(rows), [rows]);
  const errorCount = useMemo(
    () => clientErrors.reduce((n, e) => n + (Object.keys(e).length ? 1 : 0), 0),
    [clientErrors],
  );
  const blocked = errorCount > 0;

  // --- refs the popstate handler reads (avoids stale closures) ---
  const pushedRef = useRef(false);
  const closingRef = useRef(false);
  const dirtyRef = useRef(false);
  const submittingRef = useRef(false);
  dirtyRef.current = rows.length > 0 && !(result && result.failed === 0);
  submittingRef.current = submitting;

  const reset = () => {
    setRows([]);
    setServerErrors({});
    setResult(null);
    setExpanded(false);
  };

  const finalizeClose = () => {
    setOpen(false);
    reset();
    pushedRef.current = false;
  };

  const requestClose = () => {
    if (submittingRef.current) return;
    if (dirtyRef.current && !window.confirm(DISCARD_MSG)) return;
    if (pushedRef.current) {
      closingRef.current = true;
      window.history.back();
    } else {
      finalizeClose();
    }
  };

  const HINT_VALUES = new Set(["group key", "product", "variant"]);

  const isHintRow = (r) => {
    // hint row me Handle = "group key" hota hai aur baaki cells product/variant
    const handle = String(r.Handle || "")
      .trim()
      .toLowerCase();
    if (handle === "group key") return true;
    // safety: agar zyadatar cells sirf product/variant hain to bhi hint row
    const vals = Object.values(r).map((v) => String(v).trim().toLowerCase());
    const hintCount = vals.filter((v) => HINT_VALUES.has(v)).length;
    return hintCount >= 3;
  };

  useEffect(() => {
    if (!open) return;
    window.history.pushState({ importModal: true }, "");
    pushedRef.current = true;

    const onPop = () => {
      if (closingRef.current) {
        closingRef.current = false;
        finalizeClose();
        return;
      }
      if (submittingRef.current) {
        window.history.pushState({ importModal: true }, "");
        return;
      }
      if (dirtyRef.current && !window.confirm(DISCARD_MSG)) {
        window.history.pushState({ importModal: true }, "");
        return;
      }
      
      finalizeClose();
    };

    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [open]);

  const downloadSample = () => {
    const link = document.createElement("a");
    link.href = `${process.env.PUBLIC_URL}/excel/product-import-sample.xlsx`; // public folder me rakhi file
    link.download = "product-import-sample.xlsx";
    link.click();
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: "array" });
        const parsed = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], {
          defval: "",
        });

        if (!parsed.length) {
          showError("The file has no rows.");
          return;
        }

        const norm = parsed
          .filter((r) => !isHintRow(r))
          .map((r) => {
            const o = {};
            COLUMNS.forEach((c) => {
              o[c.key] = r[c.key] === undefined ? "" : r[c.key];
            });
            return o;
          });

        if (!norm.length) {
          showError(
            "No product rows found. Fill at least one product in the sample.",
          );
          return;
        }

        setRows(norm);
        setServerErrors({});
        setResult(null);
        setExpanded(true);
      } catch (err) {
        console.error(err);
        showError("Could not read this file. Use the sample .xlsx format.");
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  const editCell = (i, key, value) => {
    setRows((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], [key]: value };
      return next;
    });
    setServerErrors((prev) => {
      if (prev[i] === undefined) return prev;
      const cp = { ...prev };
      delete cp[i];
      return cp;
    });
  };

  const removeRow = (i) => {
    setRows((prev) => prev.filter((_, idx) => idx !== i));
    setServerErrors({});
    setResult(null);
  };

  const onSubmit = async () => {
    if (blocked) return;

    setSubmitting(true);
    setResult(null);
    setServerErrors({});

    try {
      const res = await ProductAPI.bulkImport({rows});
      if (res.code !== 200) {
        showError(res.message || "Import failed");
        return;
      }

      const results = res.data?.results || [];
      const srv = {};
      let ok = 0;
      results.forEach((r) => {
        if (r.ok) ok++;
        else srv[r.index] = r.message || "Server rejected this row.";
      });
      setServerErrors(srv);
      setResult({ ok, failed: Object.keys(srv).length });
      if (Object.keys(srv).length === 0 && onImported) onImported();
    } catch (err) {
      console.error(err);
      showError("Something went wrong during import");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        className="btn btn-outline-primary me-2"
        onClick={() => setOpen(true)}
      >
        <i className="ti ti-file-spreadsheet me-1" />
        Import Excel
      </button>

      {open && (
        <>
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            role="dialog"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) requestClose();
            }}
          >
            <div
              className="modal-dialog modal-dialog-centered ipx-dialog"
              style={
                expanded
                  ? { maxWidth: "100%", height: "100%", margin: 0 }
                  : { maxWidth: 480 }
              }
            >
              <div
                className="modal-content ipx-content"
                style={
                  expanded
                    ? { height: "100%", borderRadius: 0, border: "none" }
                    : {}
                }
              >
                <div className="modal-header">
                  <h5 className="modal-title">
                    {expanded
                      ? "Review products before import"
                      : "Import products from Excel"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={requestClose}
                    disabled={submitting}
                  />
                </div>

                {!expanded && (
                  <div className="modal-body">
                    <ol
                      className="text-muted small ps-3 mb-3"
                      style={{ lineHeight: 1.8 }}
                    >
                      <li>Download the sample and fill your products in it.</li>
                      <li>
                        One product = one or more rows sharing the same{" "}
                        <b>Handle</b>.
                      </li>
                      <li>Upload it back to review and edit before import.</li>
                    </ol>

                    <button
                      className="btn btn-outline-secondary w-100 mb-3"
                      onClick={downloadSample}
                    >
                      <i className="ti ti-download me-1" />
                      Download sample .xlsx
                    </button>

                    <div
                      className="ipx-drop border rounded p-4 text-center text-muted"
                      style={{ cursor: "pointer", borderStyle: "dashed" }}
                      onClick={() => fileRef.current?.click()}
                    >
                      <i
                        className="ti ti-cloud-upload d-block mb-2"
                        style={{ fontSize: 28 }}
                      />
                      Click to upload your filled .xlsx
                    </div>
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      className="d-none"
                      onChange={handleFile}
                    />
                  </div>
                )}

                {expanded && (
                  <>
                    <div
                      className="modal-body pt-2 ipx-preview d-flex flex-column"
                      style={{ overflow: "hidden" }}
                    >
                      <div className="d-flex align-items-center flex-wrap gap-2 mb-2 small">
                        <span className="badge bg-light text-dark">
                          {rows.length} rows
                        </span>
                        <span
                          className={`badge ${errorCount ? "bg-danger" : "bg-success"}`}
                        >
                          {errorCount
                            ? `${errorCount} row(s) need fixing`
                            : "All rows valid"}
                        </span>
                        {result && (
                          <span
                            className={`badge ${result.failed ? "bg-danger" : "bg-success"}`}
                          >
                            Imported {result.ok} · Failed {result.failed}
                          </span>
                        )}
                        <button
                          className="btn btn-sm btn-outline-secondary ms-auto"
                          onClick={() => setExpanded(false)}
                        >
                          Upload a different file
                        </button>
                      </div>

                      <div
                        className="table-responsive flex-grow-1"
                        style={{ overflow: "auto" }}
                      >
                        <table
                          className="table table-sm table-bordered align-middle mb-0"
                          style={{ fontSize: 12.5 }}
                        >
                          <thead
                            className="thead-light"
                            style={{ position: "sticky", top: 0, zIndex: 1 }}
                          >
                            <tr>
                              <th>#</th>
                              {COLUMNS.map((c) => (
                                <th key={c.key} className="text-nowrap">
                                  {c.key}
                                </th>
                              ))}
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((row, i) => {
                              const rowErr = clientErrors[i] || {};
                              const srvErr = serverErrors[i];
                              const bad =
                                Object.keys(rowErr).length > 0 || !!srvErr;
                              return (
                                <React.Fragment key={i}>
                                  <tr className={bad ? "table-danger" : ""}>
                                    <td>{i + 1}</td>
                                    {COLUMNS.map((c) => (
                                      <td key={c.key} style={{ padding: 2 }}>
                                        <input
                                          className={`form-control form-control-sm ${rowErr[c.key] ? "is-invalid" : ""}`}
                                          style={{
                                            minWidth:
                                              c.level === "product" ? 130 : 90,
                                            fontSize: 12.5,
                                          }}
                                          value={row[c.key] ?? ""}
                                          title={rowErr[c.key] || ""}
                                          onChange={(e) =>
                                            editCell(i, c.key, e.target.value)
                                          }
                                        />
                                      </td>
                                    ))}
                                    <td>
                                      <button
                                        className="btn btn-sm btn-link text-danger p-0"
                                        onClick={() => removeRow(i)}
                                        title="Remove row"
                                      >
                                        <i className="ti ti-trash" />
                                      </button>
                                    </td>
                                  </tr>
                                  {bad && (
                                    <tr className="table-danger">
                                      <td
                                        colSpan={COLUMNS.length + 1}
                                        className="text-danger small py-1"
                                      >
                                        {srvErr && <div className="errors text-danger">• {srvErr}</div>}
                                        {Object.entries(rowErr).map(
                                          ([f, m]) => (
                                            <div key={f} className="errors text-danger">
                                              • {f}: {m}
                                            </div>
                                          ),
                                        )}
                                      </td>
                                      <td></td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="modal-footer">
                      <button
                        className="btn btn-secondary"
                        onClick={requestClose}
                        disabled={submitting}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary ms-2"
                        onClick={onSubmit}
                        disabled={blocked || submitting}
                      >
                        {submitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Importing…
                          </>
                        ) : (
                          `Import ${rows.length} rows`
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </>
  );
}

export default ImportProducts;
